































































































































import {
  computed,
  defineComponent,
  ref,
  nextTick,
  toRefs,
  useRoute,
  onMounted,
  Ref
} from '@nuxtjs/composition-api'
import CExpandableContent from '~/components/shared/configurable/expand/CExpandableContent.vue'
import KeyFeaturesInColumns from '~/components/car/classifieds/view/partials/key-features/KeyFeaturesInColumns.vue'
import { useNamespacedStore } from '~/compositions/store'
import { TradesState, TRADES_NS } from '~/store/modules/shared/trades/state'
import CCategorySelect from '~/components/shared/configurable/category/select/CCategorySelect.vue'
import { useI18n } from '~/compositions/i18n'
import { useClassifiedForm } from '~/compositions/form'
import { formFieldProps } from '~/constants/form'
import { faEraser } from '@fortawesome/free-solid-svg-icons'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import { arraysEqual } from '~/utils/array'
import { ciTradesMatch } from '~/icons/source/regular/trades-match'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'

import { SET_CATEGORIES } from '~/store/modules/shared/trades/mutation-types'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import {
  USER_AGENT_NS,
  UserAgentState
} from '~/store/modules/shared/userAgent/state'
import { TradesCategory } from '~/models/trades/types'
import { deepJsonCopy } from '~/utils/object'
import { APP_NS, AppState } from '~/store/modules/shared/app/state'
import CInfoModal from '~/components/shared/configurable/info/CInfoModal.vue'

export default defineComponent({
  components: {
    CInfoModal,
    CExpandableContent,
    KeyFeaturesInColumns,
    CCategorySelect,
    CFormError
  },
  props: formFieldProps,
  setup(props, { emit }) {
    const { name, fieldValue, specs } = toRefs(props)
    const { error, valueChanged } = useClassifiedForm(props)
    const wrapper = ref(null)
    const errorMsg = ref(null)
    const isSuggestionModalVisible = ref(false)

    const firstFetchComplete = ref(false)

    const { getters: userAgentGetters } = useNamespacedStore<UserAgentState>(
      USER_AGENT_NS
    )
    const isMobile = computed(() => userAgentGetters('isMobile'))

    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const isSingleOrAnon = computed(() => userGetters('isSingleOrAnon'))

    const { getters } = useNamespacedStore<AppState>(APP_NS)
    const isPlot = computed(() => getters('isPlot'))

    const { state, dispatch, commit } = useNamespacedStore<TradesState>(
      TRADES_NS
    )
    const route = useRoute()
    const { t } = useI18n()
    const categories = computed(() => state.categories)
    const hasUserRichTheMaxNumberOfClassified = computed(
      () =>
        specs.value.classifieds.current_count_of_user >=
        specs.value.classifieds.max
    )

    const suggestedTrades = computed(() => {
      return specs.value?.suggested_trades?.related
    })

    const suggestedTradesInfo = computed(() => {
      return suggestedTrades.value?.map(trade => ({
        name: trade.names.join(' » '),
        ids: trade.ids
      }))
    })

    const selectedCategories: Ref<number[]> = ref([])
    const isLoading = ref(true)
    const selectedCategoriesWithouParents = ref([])

    const generateQueryParams = (selectedCat: [] = []) => {
      const q = []
      selectedCat.forEach(v => {
        if (v) {
          q.push('category=' + v)
        }
      })
      return `?${q.join('&')}`
    }

    const handleSelectedValue = async (cat: number[]) => {
      selectedCategoriesWithouParents.value = cat
      const { max } = specs.value.categories
      if (cat.length > max) {
        errorMsg.value = t(
          'you can only choose up to {num} categories'
        ).replace('{num}', max)
        await nextTick()
        selectedCategoriesWithouParents.value = cat.slice(0, cat.length - 1)
      } else if (cat.length > 0 && selectedCategories.value?.length > 0) {
        error.value = null
        selectedCategoriesWithouParents.value = cat
        if (!hasUserRichTheMaxNumberOfClassified.value) {
          errorMsg.value = null
        }
      }
      const hasCategoriesChange = arraysEqual(
        selectedCategoriesWithouParents.value,
        cat
      )

      if (cat.length !== 0 && hasCategoriesChange && !isLoading.value) {
        isLoading.value = true
        await dispatch('fetchCategories', generateQueryParams(cat))
      }
      setTimeout(() => (isLoading.value = false), 800)

      if (firstFetchComplete.value) {
        valueChanged(name.value, { categories: cat })
      }
    }

    onMounted(async () => {
      if (hasUserRichTheMaxNumberOfClassified.value) {
        errorMsg.value = t(
          'you have reached the maximum number of {max} classifieds for trading',
          { max: specs.value.classifieds.max }
        )
      }
      await fetchData()
      if (
        isPlot.value &&
        (!fieldValue.value?.categories ||
          fieldValue.value?.categories?.length === 0 ||
          route.value.query.type === 'new')
      ) {
        selectedCategories.value = []
      }
    })

    const fetchData = async () => {
      const params = fieldValue.value?.categories || []
      isLoading.value = true
      selectedCategories.value = params
      selectedCategoriesWithouParents.value = params
      let qParams = generateQueryParams(params)
      if (isPlot.value && !params.length) {
        qParams = generateQueryParams(['20004'])
      }

      await dispatch('fetchCategories', qParams)
      await nextTick()
      isLoading.value = false
      await nextTick()
      firstFetchComplete.value = true
    }

    const icons = {
      clear: faEraser,
      trades: ciTradesMatch,
      info: faInfoCircle
    }

    const handleClearCategories = () => {
      if (wrapper.value?.scrollTop) {
        wrapper.value.scrollTop = 0
      }
      selectedCategoriesWithouParents.value = []
      selectedCategories.value = []
      if (!hasUserRichTheMaxNumberOfClassified.value) {
        errorMsg.value = null
      }
      error.value = null
      valueChanged(name.value, { categories: [] })
      const parentCategoriesOnly = categories.value.map(c => ({
        ...c,
        children: [],
        selected: false
      }))

      commit(SET_CATEGORIES, parentCategoriesOnly)
    }

    const handleVisible = () => {
      emit('update')
    }

    async function setSuggestedCategory(
      ids: number[] = [],
      selected: boolean = false
    ) {
      const lastId = ids[ids.length - 1]
      const copyCategories = deepJsonCopy(categories.value)
      deselectByValue(copyCategories, lastId)

      const idsToRemove = [lastId]
      ids.forEach(id => {
        const parentNode = getParentNode(copyCategories, id)
        if (parentNode) {
          const childrenLength = parentNode.children.filter(
            (c: TradesCategory) => c.selected
          )?.length
          if (childrenLength - 1 === 0 || childrenLength === 0) {
            deselectByValue(copyCategories, parentNode.value)
            deselectByValue(copyCategories, id)
            idsToRemove.push(id)
            idsToRemove.push(parentNode.value)
          }
        }
      })

      if (!selected) {
        selectedCategories.value = selectedCategories.value.filter(
          item => !new Set(idsToRemove).has(item)
        )
      } else {
        isLoading.value = true
        try {
          await dispatch(
            'fetchCategories',
            generateQueryParams([...ids, ...selectedCategories.value] as [])
          )
        } finally {
          isLoading.value = false
        }
      }
    }
    function getParentNode(
      arr: Array<{ children: Array<TradesCategory> }>,
      value: number
    ): TradesCategory | null {
      for (const obj of arr) {
        if (obj.children) {
          if (obj.children.some(child => child.value === value)) {
            return obj as TradesCategory
          }
          const parentValue = getParentNode(
            obj.children,
            value
          ) as TradesCategory

          if (parentValue) {
            return parentValue
          }
        }
      }
      return null
    }

    function deselectByValue(arr: Array<TradesCategory>, value: number) {
      // Iterate over each object in the array
      for (const tCategory of arr) {
        // If the current object's value matches the id, deselect it
        if (tCategory.value === value) {
          tCategory.selected = false
        }

        // If the object has children, recursively call this function for each child
        if (tCategory.children) {
          deselectByValue(tCategory.children, value)
        }
      }
    }

    return {
      categories,
      selectedCategories,
      isLoading,
      icons,
      error,
      errorMsg,
      handleClearCategories,
      handleSelectedValue,
      hasUserRichTheMaxNumberOfClassified,
      wrapper,
      handleVisible,
      firstFetchComplete,
      isSingleOrAnon,
      setSuggestedCategory,
      isSuggestionModalVisible,
      suggestedTradesInfo,
      suggestedTrades,
      isMobile,
      isPlot
    }
  }
})
